/**
 * Auth context and provider (component only — useAuth is in use-auth.ts for Fast Refresh).
 */

import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { isTelegramMiniApp, getInitData } from "./telegram.ts";
import { shouldPersistSession } from "./pwa.ts";
import { BFCACHE_RESTORE_EVENT } from "./lifecycle.ts";
import {
  getStoredTokens,
  getStoredTokensAsync,
  saveTokens,
  clearTokens,
  isTokenExpired,
} from "./storage.ts";
import { errorTracking } from "./error-tracking.ts";
import { useAuthStore } from "@/stores/auth-store";
import { authLog } from "./logger";

const ROLE_CHOSEN_KEY = "onechurch_role_chosen";

function getRoleStorage() {
  return shouldPersistSession() ? localStorage : sessionStorage;
}

function getRoleChosenStorage(): "admin" | "profile" | null {
  try {
    const v = getRoleStorage().getItem(ROLE_CHOSEN_KEY);
    if (v === "admin" || v === "profile") return v;
    return null;
  } catch (err) {
    authLog.debug("Failed to get role from storage:", err);
    return null;
  }
}

function setRoleChosenStorage(value: "admin" | "profile"): void {
  try {
    getRoleStorage().setItem(ROLE_CHOSEN_KEY, value);
  } catch (err) {
    authLog.debug("Failed to save role to storage:", err);
  }
}

function clearRoleChosenStorage(): void {
  try {
    getRoleStorage().removeItem(ROLE_CHOSEN_KEY);
  } catch (err) {
    authLog.debug("Failed to clear role from storage:", err);
  }
}
import { authGraphql, isAuthNetworkError } from "./auth-client.ts";

// Telegram Widget Data type
export interface TelegramWidgetData {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

interface Role {
  code: string;
  name: string;
  description: string | null;
}

interface Account {
  id: string;
  telegramId: string | null;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email: string | null;
  hasPassword: boolean;
  roles: Role[];
  permissions: string[];
  canAccessAdmin: boolean;
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  account: Account | null;
  isMiniApp: boolean;
  error: string | null;
  /** True if API is unreachable (network error) */
  isOffline: boolean;
  /** Number of retry attempts */
  retryCount: number;
}

export type RoleChosen = "admin" | "profile" | null;

export interface AuthContextValue extends AuthState {
  /** Текущий выбранный режим для оператора/админа (профиль или админка) */
  roleChosen: RoleChosen;
  setRoleChosen: (role: "admin" | "profile") => void;
  /** Сбросить выбор режима — покажется экран выбора роли снова */
  clearRoleChosen: () => void;
  loginWithWidget: (data: TelegramWidgetData) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  /** Retry connection to API */
  retryConnection: () => void;
  /** Check if user has a specific permission */
  hasPermission: (permission: string) => boolean;
  /** Check if user has any of the specified permissions */
  hasAnyPermission: (permissions: string[]) => boolean;
  /** Check if user has a specific role */
  hasRole: (roleCode: string) => boolean;
}

const ME_QUERY = `
  query Me {
    me {
      id
      telegramId
      firstName
      lastName
      username
      email
      hasPassword
      roles {
        code
        name
        description
      }
      permissions
      canAccessAdmin
    }
  }
`;

const AUTHENTICATE_WITH_WIDGET_MUTATION = `
  mutation AuthenticateWithWidget($data: TelegramWidgetInput!) {
    authenticateWithWidget(data: $data) {
      account {
        id
        telegramId
        firstName
        lastName
        username
        email
        hasPassword
        roles {
          code
          name
          description
        }
        permissions
        canAccessAdmin
      }
      tokens {
        accessToken
        refreshToken
        expiresAt
      }
    }
  }
`;

const AUTHENTICATE_WITH_INIT_DATA_MUTATION = `
  mutation AuthenticateWithInitData($initData: String!) {
    authenticateWithInitData(initData: $initData) {
      account {
        id
        telegramId
        firstName
        lastName
        username
        email
        hasPassword
        roles {
          code
          name
          description
        }
        permissions
        canAccessAdmin
      }
      tokens {
        accessToken
        refreshToken
        expiresAt
      }
    }
  }
`;

const REFRESH_TOKEN_MUTATION = `
  mutation RefreshToken($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      accessToken
      refreshToken
      expiresAt
    }
  }
`;

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

// Retry intervals in ms: 3s, 5s, 10s, 15s, then every 15s
const RETRY_INTERVALS = [3000, 5000, 10000, 15000];
const MAX_RETRY_INTERVAL = 15000;

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    isLoading: true,
    isAuthenticated: false,
    account: null,
    isMiniApp: false,
    error: null,
    isOffline: false,
    retryCount: 0,
  });
  const [roleChosen, setRoleChosenState] =
    useState<RoleChosen>(getRoleChosenStorage);

  // Race condition protection: track if init is in progress
  const initInProgressRef = useRef(false);
  // Track current init call ID to discard stale results
  const initCallIdRef = useRef(0);

  const init = useCallback(async (isRetry = false) => {
    // Prevent concurrent init calls (race condition protection)
    if (initInProgressRef.current) {
      authLog.debug("init() already in progress, skipping");
      return;
    }
    initInProgressRef.current = true;
    
    // Increment call ID to track this specific init call
    const callId = ++initCallIdRef.current;
    
    setState((s) => ({
      ...s,
      isLoading: true,
      error: null,
      retryCount: isRetry ? s.retryCount + 1 : 0,
    }));
    const isMiniApp = isTelegramMiniApp();

    try {
      if (isMiniApp) {
        const data = await authGraphql<{ me: Account | null }>(ME_QUERY);
        
        // Check if this init call is still current (race condition protection)
        if (callId !== initCallIdRef.current) {
          authLog.debug("Stale init call detected, discarding results");
          return;
        }
        
        const account = data.me;
        // Issue and save tokens in mini-app so session persists (lock/unlock, IndexedDB)
        const initData = getInitData();
        if (account && initData) {
          try {
            const result = await authGraphql<{
              authenticateWithInitData: { account: Account; tokens: AuthTokens };
            }>(AUTHENTICATE_WITH_INIT_DATA_MUTATION, { initData });
            await saveTokens(result.authenticateWithInitData.tokens);
          } catch (err) {
            // Non-fatal: mini-app still works with initData per request
            authLog.debug("Non-fatal: mini-app token save failed:", err);
          }
        }
        setState({
          isLoading: false,
          isAuthenticated: !!account,
          account,
          isMiniApp: true,
          error: null,
          isOffline: false,
          retryCount: 0,
        });
        // Sync with Zustand store
        if (account) {
          useAuthStore.getState().setAuth(account, true);
        } else {
          useAuthStore.getState().clearAuth();
        }
        setRoleChosenState(account ? getRoleChosenStorage() : null);
        // Set user context for error tracking
        if (account) {
          errorTracking.setUser({
            id: account.id,
            telegramId: account.telegramId ?? undefined,
            accountId: account.id,
          });
        }
        return;
      }

      // Fast path: sync localStorage first (returning PWA users). Only use async when empty.
      let tokens = getStoredTokens();
      if (!tokens) {
        authLog.debug("No tokens in localStorage, checking IndexedDB/SW...");
        tokens = await getStoredTokensAsync();
      }
      authLog.debug("Tokens found:", tokens ? "yes" : "no");
      if (!tokens) {
        authLog.debug("No tokens, user is not authenticated");
        setState({
          isLoading: false,
          isAuthenticated: false,
          account: null,
          isMiniApp: false,
          error: null,
          isOffline: false,
          retryCount: 0,
        });
        setRoleChosenState(null);
        return;
      }

      if (isTokenExpired(tokens.expiresAt)) {
        try {
          const refreshData = await authGraphql<{
            refreshToken: AuthTokens;
          }>(REFRESH_TOKEN_MUTATION, {
            refreshToken: tokens.refreshToken,
          });
          await saveTokens(refreshData.refreshToken);
        } catch (err) {
          authLog.debug("Token refresh failed:", err);
          clearTokens();
          setState({
            isLoading: false,
            isAuthenticated: false,
            account: null,
            isMiniApp: false,
            error: null,
            isOffline: false,
            retryCount: 0,
          });
          // Sync with Zustand store on token refresh failure
          useAuthStore.getState().clearAuth();
          setRoleChosenState(null);
          return;
        }
      }

      const data = await authGraphql<{ me: Account | null }>(ME_QUERY);
      
      // Check if this init call is still current (race condition protection)
      if (callId !== initCallIdRef.current) {
        authLog.debug("Stale init call detected, discarding results");
        return;
      }
      
      const account = data.me;
      setState({
        isLoading: false,
        isAuthenticated: !!account,
        account,
        isMiniApp: false,
        error: null,
        isOffline: false,
        retryCount: 0,
      });
      // Sync with Zustand store
      if (account) {
        useAuthStore.getState().setAuth(account, false);
      } else {
        useAuthStore.getState().clearAuth();
      }
      setRoleChosenState(account ? getRoleChosenStorage() : null);
      // Set user context for error tracking
      if (account) {
        errorTracking.setUser({
          id: account.id,
          telegramId: account.telegramId ?? undefined,
          accountId: account.id,
        });
      }
    } catch (error) {
      // Check if this init call is still current (race condition protection)
      if (callId !== initCallIdRef.current) {
        authLog.debug("Stale init call error, discarding");
        return;
      }
      
      authLog.error("Auth init failed:", error);
      const offline = isAuthNetworkError(error);

      if (!offline) {
        clearTokens();
      }

      setState((s) => ({
        isLoading: false,
        isAuthenticated: false,
        account: null,
        isMiniApp: isTelegramMiniApp(),
        error: error instanceof Error ? error.message : "Authentication failed",
        isOffline: offline,
        retryCount: s.retryCount,
      }));
      // Sync with Zustand store
      useAuthStore.getState().clearAuth();
      setRoleChosenState(null);
    } finally {
      // Reset init in progress flag
      initInProgressRef.current = false;
    }
  }, []);

  const initialInitDone = useRef(false);
  // Initial load once
  useEffect(() => {
    if (!initialInitDone.current) {
      initialInitDone.current = true;
      init(false);
    }
  }, [init]);

  // bfcache restore / visibility: silent token refresh (no loading overlay)
  useEffect(() => {
    // Track mounted state to prevent setState after unmount (memory leak)
    let isMounted = true;

    const handler = () => {
      if (isTelegramMiniApp()) return;
      const tokens = getStoredTokens();
      if (!tokens || !isTokenExpired(tokens.expiresAt)) return;
      authGraphql<{ refreshToken: AuthTokens }>(REFRESH_TOKEN_MUTATION, {
        refreshToken: tokens.refreshToken,
      })
        .then(async (data) => {
          await saveTokens(data.refreshToken);
          return authGraphql<{ me: Account | null }>(ME_QUERY);
        })
        .then((data) => {
          // Guard against setState after unmount
          if (!isMounted) return;

          const account = data.me;
          setState((s) => ({
            ...s,
            isAuthenticated: !!account,
            account,
            isOffline: false,
          }));
          // Sync with Zustand store
          if (account) {
            useAuthStore.getState().setAuth(account, false);
          }
          setRoleChosenState(account ? getRoleChosenStorage() : null);
        })
        .catch((err) => {
          // Non-fatal: keep current state
          authLog.debug("Non-fatal: bfcache refresh failed", err);
        });
    };
    window.addEventListener(BFCACHE_RESTORE_EVENT, handler);
    return () => {
      isMounted = false;
      window.removeEventListener(BFCACHE_RESTORE_EVENT, handler);
    };
  }, []);

  // Auto-retry when offline
  useEffect(() => {
    if (!state.isOffline || state.isLoading) return;

    const interval =
      RETRY_INTERVALS[Math.min(state.retryCount, RETRY_INTERVALS.length - 1)] ||
      MAX_RETRY_INTERVAL;

    const timer = setTimeout(() => {
      init(true);
    }, interval);

    return () => clearTimeout(timer);
  }, [state.isOffline, state.isLoading, state.retryCount, init]);

  const retryConnection = useCallback(() => {
    init(true);
  }, [init]);

  const loginWithWidget = useCallback(async (data: TelegramWidgetData) => {
    setState((s) => ({ ...s, isLoading: true, error: null }));

    try {
      const result = await authGraphql<{
        authenticateWithWidget: {
          account: Account;
          tokens: AuthTokens;
        };
      }>(AUTHENTICATE_WITH_WIDGET_MUTATION, {
        data: {
          id: data.id.toString(),
          first_name: data.first_name,
          last_name: data.last_name,
          username: data.username,
          photo_url: data.photo_url,
          auth_date: data.auth_date,
          hash: data.hash,
        },
      });

      await saveTokens(result.authenticateWithWidget.tokens);

      const account = result.authenticateWithWidget.account;
      setState({
        isLoading: false,
        isAuthenticated: true,
        account,
        isMiniApp: false,
        error: null,
        isOffline: false,
        retryCount: 0,
      });
      // Sync with Zustand store
      useAuthStore.getState().setAuth(account, false);
      // Set user context for error tracking
      errorTracking.setUser({
        id: account.id,
        telegramId: account.telegramId ?? undefined,
        accountId: account.id,
      });
    } catch (error) {
      authLog.error("Widget login failed:", error);
      setState((s) => ({
        ...s,
        isLoading: false,
        error: error instanceof Error ? error.message : "Login failed",
      }));
      throw error;
    }
  }, []);

  const setRoleChosen = useCallback((role: "admin" | "profile") => {
    setRoleChosenStorage(role);
    setRoleChosenState(role);
  }, []);

  const clearRoleChosen = useCallback(() => {
    clearRoleChosenStorage();
    setRoleChosenState(null);
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    clearRoleChosenStorage();
    setRoleChosenState(null);
    setState({
      isLoading: false,
      isAuthenticated: false,
      account: null,
      isMiniApp: false,
      error: null,
      isOffline: false,
      retryCount: 0,
    });
    // Sync with Zustand store
    useAuthStore.getState().clearAuth();
    // Clear user context from error tracking
    errorTracking.setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await authGraphql<{ me: Account | null }>(ME_QUERY);
      const account = data.me;
      setState((s) => ({
        ...s,
        account,
        isAuthenticated: !!account,
      }));
      // Sync with Zustand store
      if (account) {
        useAuthStore.getState().setAuth(account, state.isMiniApp);
      } else {
        useAuthStore.getState().clearAuth();
      }
    } catch (error) {
      authLog.error("Refresh failed:", error);
    }
  }, [state.isMiniApp]);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!state.account) return false;
      return state.account.permissions.includes(permission);
    },
    [state.account]
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]): boolean => {
      const account = state.account;
      if (!account) return false;
      return permissions.some((p) => account.permissions.includes(p));
    },
    [state.account]
  );

  const hasRole = useCallback(
    (roleCode: string): boolean => {
      if (!state.account) return false;
      return state.account.roles.some((r) => r.code === roleCode);
    },
    [state.account]
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        roleChosen,
        setRoleChosen,
        clearRoleChosen,
        loginWithWidget,
        logout,
        refresh,
        retryConnection,
        hasPermission,
        hasAnyPermission,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
